import Badge from "@common/controls/Badge/component";
import { type GridViewProps } from "@common/controls/GridView/component";
import TitledGridView from "@common/controls/GridView/Titled";
import { SystemColor } from "@common/declarations";
import { useI18n } from "@common/hooks/i18n";
import { sort } from "@common/lib/array";
import date from "@common/lib/date";
import { formatMoneyDisplay } from "@common/lib/format";
import { i18nFormat } from "@common/lib/i18n";
import Network from "@common/modules/network/types";
import * as React from "react";
import { OfferLinksModal } from "./Modal/OfferLinksModal";

export const OffersWidget = (props: {
  affiliateLinks: Network.Offers.AffiliateLink[];
  offers: Network.Offers.View.UserOffer[];
  style?: React.CSSProperties;
  title?: string;
}) => {
  const i18n = useI18n();

  const [offer, setOffer] = React.useState<Network.Offers.View.UserOffer | null>(null);

  const getBadge = (offer: Network.Offers.View.UserOffer) => {
    if (!offer.startDate || !offer.endDate) return null;

    const currentDate = date.getCurrentLocalDate();
    const startDate = date.startOfDay(offer.startDate);
    const endDate = date.startOfDay(offer.endDate);

    if (currentDate < startDate) {
      const dateDifference = date.differenceInDays(startDate, currentDate) + 1;
      return (
        <Badge color={SystemColor.Gray}>
          {dateDifference === 1 ? i18n.infoStartsInOneDay : i18nFormat(i18n.infoStartsInNDays, ["N", dateDifference])}
        </Badge>
      );
    }

    if (currentDate > endDate) return <Badge color={SystemColor.Gray}>{i18n.expired}</Badge>;

    const dateDifference = date.differenceInDays(endDate, currentDate) + 1;
    return (
      <Badge color={SystemColor.Orange}>
        {dateDifference === 1 ? i18n.infoExpiresInOneDay : i18nFormat(i18n.infoExpiresInNDays, ["N", dateDifference])}
      </Badge>
    );
  };

  const sortedOffers = React.useMemo(() => {
    const currentDate = date.getCurrentLocalDate();

    const scheduledOffers = sort(
      props.offers.filter((x) => x.startDate && currentDate < date.startOfDay(x.startDate)),
      [{ key: "startDate", type: "date" }, { key: "name" }]
    );
    const activeOffers = sort(
      props.offers.filter(
        (x) =>
          x.startDate &&
          x.endDate &&
          currentDate >= date.startOfDay(x.startDate) &&
          currentDate <= date.startOfDay(x.endDate)
      ),
      [{ key: "endDate", type: "date" }, { key: "name" }]
    );
    const permanentOffers = sort(
      props.offers.filter((x) => !x.startDate || !x.endDate),
      { key: "name" }
    );
    const expiredOffers = sort(
      props.offers.filter((x) => x.endDate && currentDate > date.startOfDay(x.endDate)),
      [{ key: "startDate", type: "date" }, { key: "name" }]
    );

    return [...scheduledOffers, ...activeOffers, ...permanentOffers, ...expiredOffers];
  }, [props.offers]);

  const gridProps = React.useMemo(
    (): GridViewProps<Network.Offers.UserOffer, Network.Offers.View.UserOffer> => ({
      idKey: "offerId",
      columns: [
        {
          key: "name",
          name: i18n.name,
          dataType: "string",
          qa: "Name",
          data: (x) => (
            <>
              <span>{x.name}</span>
              {getBadge(x)}
            </>
          )
        },
        {
          key: "clickCount",
          name: i18n.clicks,
          dataType: "number",
          qa: "Clicks"
        },
        {
          key: "personalPoints",
          name: i18n.points,
          dataType: "number",
          qa: "Personal points"
        },
        {
          key: "amount",
          name: i18n.sales,
          dataType: "string",
          data: (x) => formatMoneyDisplay(x.amount, x.currencyCode),
          qa: "Commissions"
        }
      ],
      onRowClick: setOffer,
      data: sortedOffers,
      isLoading: false,
      toolbar: {
        showAdd: false,
        showSearch: false
      }
    }),
    [i18n, sortedOffers]
  );

  const urls = React.useMemo(
    (): string[] =>
      offer ? props.affiliateLinks.filter((x) => x.offerId === offer.offerId).map((x) => x.absoluteUrl) : [],
    [offer, props.affiliateLinks]
  );

  return (
    <>
      <TitledGridView
        {...gridProps}
        className="affiliate_offer_block"
        hint={i18n.infoOffers}
        title={props.title ?? i18n.affiliateLinks}
        pageSize={5}
        style={props.style}
      />

      {!!offer && <OfferLinksModal offer={offer} onClose={() => setOffer(null)} urls={urls} />}
    </>
  );
};
